import {Alert, AlertProps, LinearProgress, Tab, Tabs} from "@mui/material";
import Snackbar from '@mui/material/Snackbar';
import styles from "./AnalysisPanel.module.css";

import { SupportingContent } from "../SupportingContent";
import { ChatAppResponse } from "../../api";
import { AnalysisPanelTabs } from "./AnalysisPanelTabs";
import { ThoughtProcess } from "./ThoughtProcess";
import { useMsal } from "@azure/msal-react";
import { useLogin, getToken } from "../../authConfig";
import React, { useState, useEffect } from "react";
import { useAuthentication } from "../../authConfig";
import CustomTabPanel from "../CustomTabPanel/CustomTabPanel";
interface Props {
    className: string;
    activeTab: AnalysisPanelTabs;
    onActiveTabChanged: (tab: AnalysisPanelTabs) => void;
    activeCitation: string | undefined;
    citationHeight: string;
    answer: ChatAppResponse;
}

function a11yProps(index: number) {
    return {
        id: `analysis-panel-tab-${index}`,
        "aria-controls": `analysis-panel-tabpanel-${index}`
    };
}

export const AnalysisPanel = ({ answer, activeTab, activeCitation, citationHeight, className, onActiveTabChanged }: Props) => {
    const isDisabledThoughtProcessTab: boolean = !answer.choices[0].context.thoughts;
    const isDisabledSupportingContentTab: boolean = !answer.choices[0].context.data_points;
    const isDisabledCitationTab: boolean = !activeCitation;
    const [citation, setCitation] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [snackbar, setSnackbar] = React.useState<Pick<AlertProps, "children" | "severity"> | null>(null);
    const { isDeveloper } = useAuthentication();
    const client = useLogin ? useMsal().instance : undefined;

    const handleCloseSnackbar = () => setSnackbar(null);

    const fetchCitation = () => {
        return new Promise<void>(async (resolve, reject) => {
            const token = client ? await getToken(client) : undefined;
            if (activeCitation) {
                const originalHash = activeCitation.indexOf("#") ? activeCitation.split("#")[1] : "";
                const xhr = new XMLHttpRequest();
                xhr.open("GET", activeCitation);
                xhr.responseType = "blob";
                if (token) {
                    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
                }

                xhr.onprogress = event => {
                    if (event.lengthComputable) {
                        const percentage = (event.loaded / event.total) * 100;
                        setLoadingProgress(percentage);
                    }
                };

                xhr.onload = async () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        const citationContent = xhr.response;
                        let citationObjectUrl = URL.createObjectURL(citationContent);
                        if (originalHash) {
                            citationObjectUrl += "#" + originalHash;
                        }
                        setCitation(citationObjectUrl);
                        resolve();
                    } else {
                        setSnackbar({children: "Error: The cited document was not found; it may have been deleted or moved.", severity: "error"});
                        reject(new Error(`HTTP error: ${xhr.status}`));
                    }
                };
                xhr.onerror = () => {
                    reject(new Error("Network error"));
                };
                xhr.send();
            }
        });
    };
    useEffect(() => {
        setLoading(true);
        const fetchData = async () => {
            try {
                await fetchCitation();
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
                setLoadingProgress;
            }
        };
        fetchData();
    }, [activeCitation]);

    return (
        <div className={className}>
            <Tabs value={activeTab} onChange={(event, newValue) => newValue && onActiveTabChanged(newValue! as AnalysisPanelTabs)} aria-label="admin tabs">
                {isDeveloper() && (
                    <Tab label="Thought process" disabled={isDisabledThoughtProcessTab} value={AnalysisPanelTabs.ThoughtProcessTab} {...a11yProps(0)} />
                )}
                {isDeveloper() && (
                    <Tab
                        label="Supporting content"
                        disabled={isDisabledSupportingContentTab}
                        value={AnalysisPanelTabs.SupportingContentTab}
                        {...a11yProps(1)}
                    />
                )}
                <Tab label="Citation" disabled={isDisabledCitationTab} value={AnalysisPanelTabs.CitationTab} {...a11yProps(2)} />
            </Tabs>
            <CustomTabPanel value={activeTab} tabType={AnalysisPanelTabs.ThoughtProcessTab}>
                <ThoughtProcess thoughts={answer.choices[0].context.thoughts || []} />
            </CustomTabPanel>
            <CustomTabPanel value={activeTab} tabType={AnalysisPanelTabs.SupportingContentTab}>
                <SupportingContent supportingContent={answer.choices[0].context.data_points} />
            </CustomTabPanel>
            <CustomTabPanel value={activeTab} tabType={AnalysisPanelTabs.CitationTab}>
                {loading ? (
                    <LinearProgress variant="determinate" value={loadingProgress} />
                ) : activeCitation?.endsWith(".png") ? (
                    <img src={citation} className={styles.citationImg} />
                ) : (
                    <iframe title="Citation" src={citation} width="100%" height={citationHeight} />
                )}
            </CustomTabPanel>
            {!!snackbar && (
                <Snackbar open anchorOrigin={{ vertical: "bottom", horizontal: "center" }} onClose={handleCloseSnackbar} autoHideDuration={6000}>
                    <Alert {...snackbar} onClose={handleCloseSnackbar} />
                </Snackbar>
            )}
        </div>
    );
};
